'use client'

// StatusLine — "Pino is watching the laundry go round".
//
// One warm sentence at the very top with his face next to it. It's the most
// legible thing on screen: someone who arrives from a link and knows nothing
// should understand the whole piece from this line alone, and it's the thing
// people screenshot. A coloured mark sits in a circle on the left so the
// sentence has something beside it.

import useGameStore from '../../stores/gameStore'
import { phraseFor } from '../../lib/world/actionPhrases'
import { BIRD_NAME } from '../../lib/world/identity'
import { KawaiiIcon } from './KawaiiIcon'
import { ui, kawaii } from '../../lib/ui/theme'
import { hud } from '../../lib/ui/layout'

export function StatusLine() {
  // Select the finished phrase, not the raw action: world deltas arrive many
  // times a second and the phrase only changes when he changes what he's
  // doing, so string equality skips the rest of the re-renders.
  // "off somewhere" is true of every walk, including the one home to bed,
  // which is the one worth naming: a bird crossing town to sleep should not
  // read the same as a bird crossing town to the pictures.
  const phrase = useGameStore((s) => {
    const n = s.npcStates?.[0]
    if (n?.headingToNest && n.action === 'MOVE') return 'heading home for a sleep'
    return phraseFor(n?.action)
  })
  // The first line, composed on the server from what he is actually doing,
  // why, and who set it off (sim-server/src/world/Now.js). "Off somewhere"
  // hid the one thing a stranger wants to know, where he is going and for
  // what, while the server knew both. The action phrase stays as the
  // fallback for a sim that has not sent one.
  const line = useGameStore((s) => s.npcStates?.[0]?.now?.line || null)
  const short = useGameStore((s) => s.npcStates?.[0]?.now?.short || null)
  const asleep = useGameStore((s) => s.npcStates?.[0]?.action === 'SLEEP')
  // No bird on the wire yet. The pill used to render nothing here, so a
  // black world during a deploy or a slow connection had no sentence on it
  // at all. The world is on another machine and this says so.
  const connected = useGameStore((s) => s.connectionStatus === 'connected')
  const reaching = !phrase && !line ? (connected ? 'on his way' : 'somewhere this page cannot reach yet, still trying') : null
  if (!phrase && !line && !reaching) return null

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-50 pointer-events-none select-none max-w-[94vw]"
      style={{ bottom: `calc(${hud.statusBottom}px + env(safe-area-inset-bottom, 0px))` }}
    >
      <div className="flex items-center">
        {/* Portrait, overlapping the pill so it reads as one badge */}
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: '50%',
            background: kawaii.cream,
            border: kawaii.outline,
            boxShadow: kawaii.dropSm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: -14,
            zIndex: 1,
            overflow: 'hidden',
          }}
        >
          <KawaiiIcon name="pino" color="#8b7ad6" size={34} alt={BIRD_NAME} />
        </div>

        <div
          style={{
            background: kawaii.cream,
            border: kawaii.outline,
            borderRadius: kawaii.radiusPill,
            boxShadow: kawaii.drop,
            color: kawaii.ink,
            fontFamily: ui.font,
            fontSize: 15,
            fontWeight: 600,
            padding: '9px 20px 9px 26px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            opacity: asleep ? 0.88 : 1,
          }}
        >
          <span style={{ fontWeight: 800 }}>{BIRD_NAME}</span>
          <span style={{ color: kawaii.inkSoft }}> is </span>
          {/* A phone gets the doing; a wide screen gets the doing and the why. */}
          <span className="md:hidden">{short || phrase || reaching}</span>
          <span className="hidden md:inline">{line || phrase || reaching}</span>
        </div>
      </div>
    </div>
  )
}

export default StatusLine
